<template>
  <div class="modal-wrapper">
    <v-alert
      v-if="usages.length"
      :closable="false"
      icon="$warning"
      density="compact"
      class="py-0 mb-4 warning-block"
    >
      <div
        v-for="usage in usages"
        :key="usage.key"
        class="body2"
        :data-testid="`label-in-use-banner-${usage.key}`"
        v-html="usage.banner"
      />
    </v-alert>
    <div class="body1 mb-4">
      {{ description }}
    </div>
    <div v-for="usage in links" :key="usage.key" class="mb-2">
      <router-link
        :to="usage.route"
        target="_blank"
        class="coro-link d-inline-flex align-center"
        :data-testid="`label-in-use-link-${usage.key}`"
      >
        {{ usage.link }}
        <v-icon icon="$chevronRight" size="18"></v-icon>
      </router-link>
    </div>
    <div class="d-flex justify-end mt-10">
      <v-btn
        rounded
        color="primary"
        data-testid="label-in-use-close-btn"
        @click="$emit('dismissed')"
      >
        {{ $t("general.close") }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import uniqBy from "lodash/uniqBy";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { DeviceLabel } from "@/_store/devices-settings.module";
import { LabelUsage } from "@/constants/devices";
import { RouteName } from "@/constants/routes";

interface LabelUsageItem {
  key: LabelUsage;
  banner: string;
  link: string;
  route: { name: RouteName; query?: Record<string, string> };
}

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<DeviceLabel>>,
      required: true,
    },
  },
  emits: ["dismissed"],
  setup(props) {
    const i18n = useI18n();

    const usages = computed<LabelUsageItem[]>(() => {
      const label = props.config.item;
      const swgUsageCount = label.swgUsageCount ?? 0;
      const ztnaResourceUsageCount = label.ztnaResourceUsageCount ?? 0;

      return [
        {
          condition: swgUsageCount > 0,
          key: LabelUsage.SWG_LISTS,
          banner: i18n.t(
            "modals.labelInUse.banner.swgLists",
            { count: swgUsageCount },
            swgUsageCount
          ),
          link: i18n.t("modals.labelInUse.viewLists"),
          route: { name: RouteName.SECURE_WEB_GATEWAY_SETTINGS, query: { label: label.id } },
        },
        {
          condition: Boolean(label.usedBySwgAllowOnlyFiltering),
          key: LabelUsage.SWG_ALLOW_ONLY_FILTERING,
          banner: i18n.t("modals.labelInUse.banner.swgAllowOnlyFiltering"),
          link: i18n.t("modals.labelInUse.viewSwgSettings"),
          route: { name: RouteName.SECURE_WEB_GATEWAY_SETTINGS },
        },
        {
          condition: Boolean(label.usedByVpnSplitTunnel),
          key: LabelUsage.VPN_SPLIT_TUNNEL,
          banner: i18n.t("modals.labelInUse.banner.vpnSplitTunnel"),
          link: i18n.t("modals.labelInUse.viewVpnSettings"),
          route: { name: RouteName.NETWORK_VPN_TAB },
        },
        {
          condition: Boolean(label.usedByVpnFullTunnel),
          key: LabelUsage.VPN_FULL_TUNNEL,
          banner: i18n.t("modals.labelInUse.banner.vpnFullTunnel"),
          link: i18n.t("modals.labelInUse.viewVpnSettings"),
          route: { name: RouteName.NETWORK_VPN_TAB },
        },
        {
          condition: ztnaResourceUsageCount > 0,
          key: LabelUsage.ZTNA_RESOURCES,
          banner: i18n.t(
            "modals.labelInUse.banner.ztnaResources",
            { count: ztnaResourceUsageCount },
            ztnaResourceUsageCount
          ),
          link: i18n.t("modals.labelInUse.viewZtnaResources"),
          route: { name: RouteName.ZTNA_TAB },
        },
      ]
        .filter((usage) => usage.condition)
        .map(({ condition, ...usage }) => usage);
    });

    const links = computed(() => uniqBy(usages.value, (usage) => usage.link));

    const description = computed(() =>
      usages.value.length === 1 && usages.value[0].key === LabelUsage.SWG_LISTS
        ? i18n.t("modals.labelInUse.description")
        : i18n.t("modals.labelInUse.descriptionGeneral")
    );

    return {
      usages,
      links,
      description,
    };
  },
});
</script>

<style scoped lang="scss">
.coro-link {
  text-decoration: none !important;

  .v-icon:before {
    color: rgb(var(--v-theme-link)) !important;
  }
}
</style>
